import React, { ComponentType, forwardRef } from "react";

import { ForwardRefType, KnownKeys } from "@multiweb/types/react";
import { LocalizationLoader } from "UI/UI.React/components/LocalizationLoader";
import { LocalizationProvider } from "UI/UI.React/components/LocalizationProvider";

type TLocKey = { locKey: string | undefined};
/** Typ obsahující výslednou lokalizaci (text a její popisek) */
type LocalizationResult = {text: string; title: string};
/**
 * Typ, který slouží k namapování props komponenty na `locKey`, který se použije pro získání lokalizace
 */
export type MapLocKey<TComponentProps> = (props: React.PropsWithChildren<TComponentProps>) => TLocKey;
/**
 * Typ, který mapuje získanou lokalizaci do props lokalizované komponenty.
 * `text` obsahuje překlad lokalizačního klíče.
 * `title` obsahuje popisek lokalizačního klíče.
 */
export type MapLocalizationToProps<TComponentProps> = (localization: LocalizationResult) => TComponentProps;

/**
 * HOC, které lokalizuje komponentu. Přijímá argument `locKey` na základě kterého vrátí text a popisek, který se namapuje do props obalované komponenty.
 * @param mapLocalizationToProps Typ, který slouží k namapování props komponenty na `locKey`, který se použije pro získání lokalizace
 * @param mapLocKey Typ, který mapuje získanou lokalizaci do props lokalizované komponenty
 * @returns Lokalizovaná komponenta
 */
export const withLocalization =
<TStateProps, TComponentProps> (mapLocalizationToProps: MapLocalizationToProps<TStateProps>, mapLocKey: MapLocKey<Omit<TComponentProps, KnownKeys<TStateProps>>>) =>
        (Component: ComponentType<TComponentProps>): ForwardRefType<HTMLElement, Omit<TComponentProps, KnownKeys<TStateProps>>> => {
            //Záměrně obcházíme typovou kontrolu. Protože `LocalizedComponent` potřebují mít na vstupu celé props. Ale z metody je potřeba vrátit komponentu bez props, které byly naplněny tady a nebyly pak vynucovány
            const LocalizedComponent = forwardRef<HTMLElement, TComponentProps>((props, ref) => {
                const locKey = mapLocKey(props).locKey || "";
                return (
                    <LocalizationLoader locKey={locKey}>
                        <LocalizationProvider locKey={locKey}>
                            {({ text, title }) => <Component {...mapLocalizationToProps({ text, title })} {...props}
                                ref={ref}/>}
                        </LocalizationProvider>
                    </LocalizationLoader>
                );
            }) as ForwardRefType<HTMLElement, Omit<TComponentProps, KnownKeys<TStateProps>>>;
            return LocalizedComponent;
        };
